import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Finish, getFinishes, MaterialProperties } from '@steelbuy/ts-shared';
import { FormItem, InputSelect } from '@steelbuy/ui-primitive';

import { AnyValidationProps } from '../validation/Validation';
import { useRequiredFieldValidator } from '../validation/ValidationHooks';

export const MaterialFinishSelection = ({
    finishSelected,
    forceValidation,
    material,
    required,
    getDefaultValue,
}: {
    material: MaterialProperties;
    finishSelected: (finish?: Finish) => void;
    getDefaultValue?: (material: MaterialProperties) => Finish | undefined;
} & AnyValidationProps) => {
    const { t } = useTranslation(['uiDomain', 'domainModel']);
    const requiredFieldValidator = useRequiredFieldValidator();
    const hasUserDeselected = useRef(false);

    const availableFinishes = getFinishes(material)?.slice()?.sort();
    const { finish } = material;

    useEffect(() => {
        const defaultValue = getDefaultValue?.(material);
        if (
            (finish !== undefined && !availableFinishes.includes(finish)) ||
            (finish === undefined &&
                defaultValue &&
                availableFinishes.includes(defaultValue) &&
                !hasUserDeselected.current)
        ) {
            finishSelected(defaultValue);
        }
    }, [finish, finishSelected, availableFinishes]);

    if (availableFinishes.length === 0) {
        return null;
    }

    const materialFinishOptions = availableFinishes.map((materialFinish) => ({
        label: t(`domainModel:material.finish.value.${materialFinish}`),
        value: materialFinish,
    }));

    return (
        <FormItem header={t('domainModel:material.finish.name')} isMandatory={required}>
            <InputSelect
                options={materialFinishOptions}
                placeholder={t('uiDomain:materialSelection.placeholder')}
                value={finish}
                onSelect={(value) => {
                    hasUserDeselected.current = !value;
                    finishSelected(value);
                }}
                validators={required ? [requiredFieldValidator] : undefined}
                forceValidation={forceValidation}
                searchable
            />
        </FormItem>
    );
};
